import { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils.js';
import Block from './Block.jsx';
import SlashMenu from './SlashMenu.jsx';

const BlockEditor = ({ blocks, onChange, onCreatePage }) => {
  const [slashMenu, setSlashMenu] = useState({ isOpen: false, blockId: null, position: null, showAbove: false });
  const editorRef = useRef(null);

  const updateBlock = useCallback((id, updates) => {
    console.log('[BlockEditor] Update block:', id, updates);
    onChange((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...updates } : b))
    );
  }, [onChange]);

  const addBlockAfter = useCallback((afterId, newBlock) => {
    onChange((prev) => {
      const index = prev.findIndex((b) => b.id === afterId);
      const newId = Math.random().toString(36).substr(2, 9);
      const block = { ...newBlock, id: newId, shouldFocus: true };
      const next = [...prev];
      next.splice(index + 1, 0, block);
      return next;
    });
  }, [onChange]);

  const deleteBlock = useCallback((id) => {
    onChange((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((b) => b.id !== id);
    });
  }, [onChange]);

  const openSlashMenu = useCallback((blockId) => {
    console.log('[BlockEditor] Opening slash menu for block:', blockId);
    const index = blocks.findIndex((b) => b.id === blockId);
    const blockElement = document.querySelector(`[data-block-index="${index}"]`);
    if (blockElement) {
      const rect = blockElement.getBoundingClientRect();
      const editorRect = editorRef.current?.getBoundingClientRect();
      if (editorRect) {
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        let top = rect.bottom - editorRect.top + 4;
        let showAbove = false;
        
        // Position menu above block if there is not enough space below (less than 320px)
        // and more space above.
        if (spaceBelow < 320 && spaceAbove > spaceBelow) {
          showAbove = true;
          top = rect.top - editorRect.top - 4;
        }

        setSlashMenu({
          isOpen: true,
          blockId: blockId,
          showAbove,
          position: {
            top,
            left: rect.left - editorRect.left,
          },
        });
      }
    }
  }, [blocks]);

  const handleKeyDown = useCallback((e, block) => {
    console.log('[BlockEditor] KeyDown:', e.key, 'block:', block.id, 'content:', block.content);
    
    if (e.key === '/' && block.content === '') {
      openSlashMenu(block.id);
    }
  }, [openSlashMenu]);

  const handleSlashSelect = useCallback((command) => {
    console.log('[BlockEditor] Slash command selected:', command);
    const { blockId } = slashMenu;
    console.log('[BlockEditor] Target block ID:', blockId);
    
    if (command.type === 'page') {
      console.log('[BlockEditor] Creating page');
      if (onCreatePage) {
        onCreatePage();
      }
      setSlashMenu({ isOpen: false, blockId: null, position: null, showAbove: false });
      return;
    }

    console.log('[BlockEditor] Creating Block Type:', command.type);
    onChange((prev) => {
      const newBlocks = prev.map((b) => {
        if (b.id === blockId) {
          console.log('[BlockEditor] Block Updated:', b.id, '->', command.type);
          return { ...b, type: command.type, content: '', shouldFocus: true };
        }
        return b;
      });
      console.log('[BlockEditor] New blocks:', newBlocks);
      return newBlocks;
    });
    setSlashMenu({ isOpen: false, blockId: null, position: null, showAbove: false });
    console.log('[BlockEditor] Slash menu closed');
  }, [slashMenu, onCreatePage, onChange]);

  return (
    <div ref={editorRef} className="relative">
      <div className="space-y-1">
        {blocks.map((block, index) => (
          <Block
            key={block.id}
            block={block}
            index={index}
            totalBlocks={blocks.length}
            onUpdate={updateBlock}
            onDelete={deleteBlock}
            onAddAfter={addBlockAfter}
            onKeyDown={handleKeyDown}
            onSlashTrigger={openSlashMenu}
          />
        ))}
      </div>
      <SlashMenu
        isOpen={slashMenu.isOpen}
        onClose={() => {
          console.log('[BlockEditor] Slash menu closed');
          setSlashMenu({ isOpen: false, blockId: null, position: null, showAbove: false });
        }}
        onSelect={handleSlashSelect}
        position={slashMenu.position}
        showAbove={slashMenu.showAbove}
      />
    </div>
  );
};

export default BlockEditor;
